import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { signal } from "@preact/signals-react";

import restOrder from "./Sale";
import { useStoreRest, getIsTagsActive } from "./storeRest";
import { getStorage, setStorage } from "store/local";
import {
  ReceiptAddIcon,
  SquaresPlusIcon,
  TableListIcon,
} from "components/Icons";
import Button from "componentsv2/button/Button";
import QuickForm from "components/Meta/QuickForm";
import { cn } from "tools/ui";
import TableView from "./components/TableView";
import SelectField from "./components/select";
import useMainStore from "store/mainStore";
import ListView from "./components/ListView";
import { useViewModeStore } from "./storeView";
import AgentModal from "./components/AgentModal";

const openModal = signal(false);

const SaleOrder = ({ config }) => {
  const sessionUser = getStorage("ctxSession");
  const [order, setOrder] = useState({});
  const [pendingAction, setPendingAction] = useState(null);
  const { setRecord, newRecord } = useMainStore();
  const { viewMode, setViewMode } = useViewModeStore();
  const [view, setView] = useState(viewMode);
  const {
    initShop,
    setIsFilterByShop,
    shopAccess,
    setShopAccess,
    defaultShop,
    agentRequired,
    requestCodeSaleEdit,
    agents,
  } = useStoreRest();

  const { data } = useQuery({
    queryKey: ["isTagsActive"],
    queryFn: getIsTagsActive,
  });

  const optionsShop =
    shopAccess?.map((shop) => ({
      label: shop.name,
      value: shop.id,
    })) || [];

  async function onChangeShop(e) {
    let shop = e.target.value;
    shop = Number(shop);
    await setShopAccess(null, shop);
    await initShop(shop);
  }

  async function onChangeView(_, action, record) {
    const view = action === "return" ? viewMode : "form";
    if (action === "add") {
      const record_ = { ...record, shop: defaultShop };
      if (agentRequired || requestCodeSaleEdit) {
        openModal.value = true;
        setOrder(record_);
        return;
      } else {
        await newRecord(ctxView, null, record_, record_);
      }
    } else if (action === "edit") {
      if (requestCodeSaleEdit) {
        openModal.value = true;
        setOrder(record);
        setPendingAction("edit");
        return;
      }
      await setRecord(record.id, ctxView.model, ctxView);
    }
    setView(view);
  }

  async function handleAgent(agent) {
    const session = getStorage("ctxSession");
    const agentUserId = agent?.user
      ? Array.isArray(agent.user)
        ? agent.user[0]
        : agent.user
      : null;

    if (agentUserId && agentUserId !== session.user) {
      session.agentUser = agentUserId;
    }
    session.agent = agent;
    setStorage("ctxSession", session);

    if (pendingAction === "edit") {
      await setRecord(order.id, ctxView.model, ctxView);
    } else {
      const record = { ...order, agent: agent };
      await newRecord(ctxView, null, record, record);
    }
    setPendingAction(null);
    openModal.value = false;
    setView("form");
  }

  const ctxView = restOrder.ctxView({ ...config, productTagActive: data });

  useEffect(() => {
    return () => {
      const session = getStorage("ctxSession");
      delete session.agentUser;
      setStorage("ctxSession", session);
    };
  }, []);

  useEffect(() => {
    initShop(sessionUser.shop);
    setIsFilterByShop();
    if (!shopAccess) setShopAccess(sessionUser);
  }, [sessionUser.shop]);

  function handleViewPrefer(view) {
    setViewMode(view);
    setView(view);
  }

  return (
    <>
      {view !== "form" && (
        <div className="w-full relative px-4 py-2 m-4">
          <div className="flex flex-wrap justify-between">
            <div className="flex space-x-4 items-center">
              <span className="truncate md:text-4xl text-2xl font-light text-zinc-500">
                PEDIDO |
              </span>
              <span>
                <SelectField
                  name="shop"
                  options={optionsShop}
                  value={defaultShop || ""}
                  onChange={onChangeShop}
                  className={
                    " bg-transparent font-semibold text-zinc-700 border-none focus:outline-none rounded-md"
                  }
                />
              </span>
            </div>
            <div className="flex space-x-2 items-center">
              <div
                className={cn(
                  "transition-all duration-300 ease-out opacity-0 scale-95",
                  view === "list" && "opacity-100 scale-100",
                )}
              >
                <Button
                  name="add"
                  content="sale.sale.order_button_new_sale"
                  onClick={onChangeView}
                  style="bg-amber-300 border-none text-gray-900 transition"
                  iconLeft={
                    <ReceiptAddIcon className="h-6 w-6 fill-gray-900 hover:opacity-90 disabled:opacity-50" />
                  }
                />
              </div>

              <Button
                style={cn(
                  "!w-12 border-none",
                  view === "kanbas"
                    ? "bg-amber-100 ring-2 ring-amber-200"
                    : "bg-gray-200 ",
                )}
                onClick={() => handleViewPrefer("kanbas")}
              >
                <SquaresPlusIcon className={"h-6 w-6"} />
              </Button>
              <Button
                style={cn(
                  "!w-12 border-none",
                  view === "list"
                    ? "bg-amber-100 ring-2 ring-amber-200"
                    : "bg-gray-200 ",
                )}
                onClick={() => handleViewPrefer("list")}
              >
                <TableListIcon className={"h-6 w-6 "} />
              </Button>
            </div>
          </div>

          <div className="mt-4">
            {view === "kanbas" && (
              <TableView
                ctxView={ctxView}
                defaultShop={defaultShop}
                onChangeView={onChangeView}
              />
            )}
            {view === "list" && (
              <ListView
                onChangeView={onChangeView}
                ctxView={ctxView}
                defaultShop={defaultShop}
              />
            )}
          </div>
        </div>
      )}
      {view === "form" && (
        <div className="w-full">
          <QuickForm
            ctxView={ctxView}
            level="main"
            onChangeView={onChangeView}
          />
        </div>
      )}
      <AgentModal
        onConfirm={handleAgent}
        openModal={openModal}
        onClose={() => {
          openModal.value = false;
        }}
        agents={agents}
      />
    </>
  );
};

export default SaleOrder;
